// services/lead-generation/data-sources/EURegistryAPI.js
// European company registry data source integrations - Client Side

/**
 * German Handelsregister API Service
 * Note: Official API limited - consider premium services like Northdata.de
 */
export class GermanRegistryAPI {
  constructor() {
    this.baseUrl = 'https://www.handelsregister.de';
    this.rateLimit = 1000; // ms between requests
    this.lastRequest = 0;
  }

  async searchCompanies({ query, maxResults = 50, location = null }) {
    try {
      await this.waitForRateLimit();
      
      // For production: Replace with actual German registry API
      // Current implementation is a placeholder for the structure
      console.log(`[German Registry] Searching for: ${query}`);
      
      // Mock data structure matching German companies
      const mockResults = this.generateMockGermanCompanies(query, maxResults);
      
      return {
        companies: mockResults,
        source: 'German Handelsregister',
        totalResults: mockResults.length,
        query: query,
        timestamp: new Date().toISOString()
      };
      
    } catch (error) {
      console.error('German registry search failed:', error);
      throw new Error(`German registry error: ${error.message}`);
    }
  }

  async getCompanyDetails(registryNumber) {
    try {
      await this.waitForRateLimit();
      
      console.log(`[German Registry] Getting details for: ${registryNumber}`);
      
      // Placeholder for actual API integration
      return {
        companyNumber: registryNumber,
        name: `Company ${registryNumber}`,
        country: 'Germany',
        source: 'German Handelsregister',
        detailedData: true
      };
      
    } catch (error) {
      throw new Error(`German company details failed: ${error.message}`);
    }
  }

  generateMockGermanCompanies(query, maxResults) {
    const germanCities = ['Berlin', 'Munich', 'Frankfurt', 'Hamburg', 'Cologne', 'Stuttgart', 'Düsseldorf'];
    const legalForms = ['GmbH', 'AG', 'KG', 'OHG'];
    const industries = ['Manufacturing', 'Technology', 'Financial Services', 'Retail', 'Energy'];
    
    return Array.from({ length: Math.min(maxResults, 25) }, (_, i) => ({
      id: `de_hrb_${Date.now()}_${i}`,
      name: `${query} ${legalForms[i % legalForms.length]} ${i + 1}`,
      registryNumber: `HRB ${100000 + i}`,
      country: 'Germany',
      city: germanCities[i % germanCities.length],
      postalCode: `${10000 + Math.floor(Math.random() * 80000)}`,
      legalForm: legalForms[i % legalForms.length],
      status: 'Active',
      industry: industries[i % industries.length],
      employees: Math.floor(Math.random() * 800) + 50,
      turnover: Math.floor(Math.random() * 80000000) + 5000000, // €5M-85M
      vatNumber: `DE${Math.floor(Math.random() * 900000000) + 100000000}`,
      source: 'German Handelsregister',
      lastUpdated: new Date().toISOString()
    }));
  }

  async waitForRateLimit() {
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequest;
    
    if (timeSinceLastRequest < this.rateLimit) {
      await new Promise(resolve => setTimeout(resolve, this.rateLimit - timeSinceLastRequest));
    }
    
    this.lastRequest = Date.now();
  }
}

/**
 * French SIRENE (INSEE) API Service
 * Official INSEE API for French company data
 */
export class FrenchSIRENEAPI {
  constructor() {
    this.baseUrl = 'https://api.insee.fr/entreprises/sirene/V3';
    this.consumerKey = process.env.REACT_APP_INSEE_CONSUMER_KEY;
    this.consumerSecret = process.env.REACT_APP_INSEE_CONSUMER_SECRET;
    this.accessToken = null;
    this.tokenExpiry = null;
  }

  async authenticate() {
    try {
      if (!this.consumerKey || !this.consumerSecret) {
        throw new Error('INSEE API credentials not configured - add REACT_APP_INSEE_CONSUMER_KEY and REACT_APP_INSEE_CONSUMER_SECRET');
      }

      const response = await fetch('https://api.insee.fr/token', {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${btoa(this.consumerKey + ':' + this.consumerSecret)}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: 'grant_type=client_credentials'
      });

      const data = await response.json();
      
      if (!data.access_token) {
        throw new Error('Failed to get INSEE access token');
      }

      this.accessToken = data.access_token;
      this.tokenExpiry = Date.now() + (data.expires_in * 1000) - 60000; // Refresh 1 min early
      
      console.log('[French SIRENE] Authentication successful');
      return this.accessToken;
      
    } catch (error) {
      console.error('INSEE authentication failed:', error);
      throw new Error(`INSEE auth error: ${error.message}`);
    }
  }

  async ensureAuthenticated() {
    if (!this.accessToken || Date.now() >= this.tokenExpiry) {
      await this.authenticate();
    }
    return this.accessToken;
  }

  async searchCompanies({ query, maxResults = 50, activeOnly = true }) {
    try {
      await this.ensureAuthenticated();
      
      const searchFilters = [
        `denominationUniteLegale:${encodeURIComponent(query)}*`
      ];
      
      if (activeOnly) {
        searchFilters.push('etatAdministratifUniteLegale:A'); // Active companies only
      }
      
      const searchQuery = searchFilters.join(' AND ');
      const searchUrl = `${this.baseUrl}/siret?q=${searchQuery}&nombre=${maxResults}&debut=1`;
      
      console.log(`[French SIRENE] Searching: ${searchUrl}`);
      
      const response = await fetch(searchUrl, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`INSEE API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      // Transform INSEE data to standard format
      const companies = this.transformINSEEData(data.etablissements || []);
      
      return {
        companies,
        source: 'French SIRENE',
        totalResults: data.header?.total || companies.length,
        query: query,
        timestamp: new Date().toISOString()
      };
      
    } catch (error) {
      console.error('French SIRENE search failed:', error);
      throw new Error(`French registry error: ${error.message}`);
    }
  }

  async getCompanyDetails(siret) {
    try {
      await this.ensureAuthenticated();
      
      const response = await fetch(`${this.baseUrl}/siret/${siret}`, {
        headers: {
          'Authorization': `Bearer ${this.accessToken}`,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`INSEE API error: ${response.status}`);
      }

      const data = await response.json();
      return this.transformINSEEData([data.etablissement])[0];
      
    } catch (error) {
      throw new Error(`French company details failed: ${error.message}`);
    }
  }

  transformINSEEData(etablissements) {
    return etablissements.map(etablissement => {
      const uniteLegale = etablissement.uniteLegale || {};
      
      return {
        id: `fr_${etablissement.siret}`,
        name: uniteLegale.denominationUniteLegale || 
              etablissement.denominationUsuelleEtablissement || 
              'Unknown Company',
        registryNumber: etablissement.siret,
        siren: uniteLegale.siren,
        country: 'France',
        city: etablissement.libelleCommuneEtablissement,
        postalCode: etablissement.codePostalEtablissement,
        address: this.buildFrenchAddress(etablissement),
        legalForm: this.mapFrenchLegalForm(uniteLegale.categorieJuridiqueUniteLegale),
        status: etablissement.etatAdministratifEtablissement === 'A' ? 'Active' : 'Inactive',
        industry: this.mapFrenchIndustry(etablissement.activitePrincipaleEtablissement),
        industryCode: etablissement.activitePrincipaleEtablissement,
        employees: this.estimateEmployees(uniteLegale.trancheEffectifsUniteLegale),
        employeeSizeCode: uniteLegale.trancheEffectifsUniteLegale,
        registrationDate: uniteLegale.dateCreationUniteLegale,
        lastModified: etablissement.dateDernierTraitementEtablissement,
        source: 'French SIRENE',
        lastUpdated: new Date().toISOString(),
        // ESG-relevant data points
        esgMetadata: {
          isListedCompany: this.checkIfListed(etablissement),
          industryESGRisk: this.assessIndustryESGRisk(etablissement.activitePrincipaleEtablissement),
          complianceDeadline: this.getCSRDDeadline(uniteLegale, etablissement)
        }
      };
    });
  }

  buildFrenchAddress(etablissement) {
    const parts = [
      etablissement.numeroVoieEtablissement,
      etablissement.indiceRepetitionEtablissement,
      etablissement.typeVoieEtablissement,
      etablissement.libelleVoieEtablissement
    ].filter(Boolean);
    
    return parts.join(' ');
  }

  mapFrenchLegalForm(code) {
    const legalFormMap = {
      '5498': 'SARL',
      '5499': 'SARL unipersonnelle',
      '5505': 'SA à conseil d\'administration',
      '5710': 'SAS',
      '5720': 'SASU'
    };
    return legalFormMap[code] || `Code: ${code}`;
  }

  mapFrenchIndustry(code) {
    // NAF/NACE code mapping to readable industry names
    const industryMap = {
      '01': 'Agriculture',
      '10': 'Food Manufacturing', 
      '20': 'Chemical Manufacturing',
      '25': 'Metal Products Manufacturing',
      '35': 'Energy Supply',
      '41': 'Construction',
      '46': 'Wholesale Trade',
      '47': 'Retail Trade',
      '62': 'Computer Programming',
      '70': 'Management Consulting'
    };
    
    const prefix = code?.substring(0, 2);
    return industryMap[prefix] || `NACE: ${code}`;
  }

  estimateEmployees(trancheEffectifs) {
    const sizeMap = {
      '00': 0, '01': 2, '02': 4, '03': 8, '11': 15, '12': 35,
      '21': 75, '22': 150, '31': 300, '32': 375, '41': 750,
      '42': 1500, '51': 3500, '52': 7500, '53': 12500
    };
    return sizeMap[trancheEffectifs] || 0;
  }

  checkIfListed(etablissement) {
    // Logic to determine if company is publicly listed
    // This would need specific indicators from SIRENE data
    return false; // Placeholder
  }

  assessIndustryESGRisk(industryCode) {
    const highRiskCodes = ['01', '05', '06', '07', '08', '09', '10', '20', '35'];
    const mediumRiskCodes = ['25', '41', '49', '50', '51'];
    
    const prefix = industryCode?.substring(0, 2);
    
    if (highRiskCodes.includes(prefix)) return 'HIGH';
    if (mediumRiskCodes.includes(prefix)) return 'MEDIUM';
    return 'LOW';
  }

  getCSRDDeadline(uniteLegale, etablissement) {
    const employees = this.estimateEmployees(uniteLegale.trancheEffectifsUniteLegale);
    
    if (employees >= 500) return '2025'; // Large companies
    if (this.checkIfListed(etablissement)) return '2026'; // Listed SMEs
    return '2028'; // Other companies if applicable
  }
}

/**
 * Dutch KVK API Service
 * Official Netherlands Chamber of Commerce API
 */
export class DutchKVKAPI {
  constructor() {
    this.baseUrl = 'https://api.kvk.nl/api/v1';
    this.apiKey = process.env.REACT_APP_KVK_API_KEY;
    this.rateLimit = 1000;
    this.lastRequest = 0;
  }

  async searchCompanies({ query, maxResults = 50, activeOnly = true }) {
    try {
      if (!this.apiKey) {
        throw new Error('KVK API key not configured - add REACT_APP_KVK_API_KEY to environment');
      }

      await this.waitForRateLimit();
      
      let searchUrl = `${this.baseUrl}/zoeken?naam=${encodeURIComponent(query)}&aantal=${maxResults}`;
      
      if (activeOnly) {
        searchUrl += '&status=Actief';
      }
      
      console.log(`[Dutch KVK] Searching: ${searchUrl}`);
      
      const response = await fetch(searchUrl, {
        headers: {
          'apikey': this.apiKey,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`KVK API error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      // Transform KVK data to standard format
      const companies = this.transformKVKData(data.resultaten || []);
      
      return {
        companies,
        source: 'Netherlands KVK',
        totalResults: data.totaal || companies.length,
        query: query,
        timestamp: new Date().toISOString()
      };
      
    } catch (error) {
      console.error('Dutch KVK search failed:', error);
      throw new Error(`Dutch registry error: ${error.message}`);
    }
  }

  async getCompanyDetails(kvkNumber) {
    try {
      if (!this.apiKey) {
        throw new Error('KVK API key not configured');
      }

      await this.waitForRateLimit();
      
      const response = await fetch(`${this.baseUrl}/basisprofielen/${kvkNumber}`, {
        headers: {
          'apikey': this.apiKey,
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`KVK API error: ${response.status}`);
      }

      const data = await response.json();
      return this.transformKVKData([data])[0];
      
    } catch (error) {
      throw new Error(`Dutch company details failed: ${error.message}`);
    }
  }

  transformKVKData(companies) {
    return companies.map(company => ({
      id: `nl_${company.kvkNummer}`,
      name: company.handelsnaam || company.naam || 'Unknown Company',
      registryNumber: company.kvkNummer,
      country: 'Netherlands',
      city: company.plaats,
      postalCode: company.postcode,
      address: this.buildDutchAddress(company),
      legalForm: company.rechtsvorm,
      status: company.status === 'Actief' ? 'Active' : company.status,
      industry: company.hoofdactiviteit,
      employees: company.personen || 0,
      website: company.website,
      source: 'Netherlands KVK',
      kvkNumber: company.kvkNummer,
      lastUpdated: new Date().toISOString(),
      // ESG-relevant metadata
      esgMetadata: {
        industryESGRisk: this.assessDutchIndustryRisk(company.hoofdactiviteit),
        complianceDeadline: this.getDutchCSRDDeadline(company.personen)
      }
    }));
  }

  buildDutchAddress(company) {
    const parts = [
      company.straatnaam,
      company.huisnummer,
      company.huisletter,
      company.huisnummerToevoeging
    ].filter(Boolean);
    
    return parts.join(' ');
  }

  assessDutchIndustryRisk(activity) {
    const highRiskKeywords = ['manufacturing', 'energy', 'chemical', 'oil', 'mining'];
    const mediumRiskKeywords = ['construction', 'transport', 'logistics'];
    
    const activityLower = (activity || '').toLowerCase();
    
    if (highRiskKeywords.some(keyword => activityLower.includes(keyword))) {
      return 'HIGH';
    }
    if (mediumRiskKeywords.some(keyword => activityLower.includes(keyword))) {
      return 'MEDIUM';
    }
    return 'LOW';
  }

  getDutchCSRDDeadline(employees) {
    if (employees >= 500) return '2025';
    return '2026'; // Listed SMEs or future expansion
  }

  async waitForRateLimit() {
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequest;
    
    if (timeSinceLastRequest < this.rateLimit) {
      await new Promise(resolve => setTimeout(resolve, this.rateLimit - timeSinceLastRequest));
    }
    
    this.lastRequest = Date.now();
  }
}

/**
 * EU VIES VAT Validation Service
 * Validates VAT numbers across all EU countries
 */
export class EUVIESService {
  constructor() {
    this.baseUrl = 'https://ec.europa.eu/taxation_customs/vies/rest-api';
    this.rateLimit = 1000;
    this.lastRequest = 0;
  }

  async validateVATNumber(vatNumber, countryCode) {
    try {
      await this.waitForRateLimit();
      
      const cleanVatNumber = vatNumber.replace(/[^A-Z0-9]/gi, '');
      const url = `${this.baseUrl}/check-vat-number?vatNumber=${cleanVatNumber}&countryCode=${countryCode.toUpperCase()}`;
      
      console.log(`[EU VIES] Validating VAT: ${countryCode}${cleanVatNumber}`);
      
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'Digital-Trent-ESG-LeadGen/1.0'
        }
      });

      if (!response.ok) {
        throw new Error(`VIES API error: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.valid) {
        return {
          id: `vies_${countryCode}_${cleanVatNumber}`,
          name: data.name || 'Company Name Not Available',
          vatNumber: `${countryCode.toUpperCase()}${cleanVatNumber}`,
          country: this.getCountryFromCode(countryCode),
          address: data.address || '',
          status: 'Active',
          source: 'EU VIES',
          validVAT: true,
          requestDate: data.requestDate,
          lastUpdated: new Date().toISOString()
        };
      }
      
      return null;
      
    } catch (error) {
      console.error('VIES validation failed:', error);
      return null;
    }
  }

  async batchValidateVAT(vatNumbers) {
    const results = [];
    
    for (const vatData of vatNumbers) {
      try {
        const result = await this.validateVATNumber(vatData.number, vatData.country);
        results.push({
          input: vatData,
          result: result,
          valid: !!result
        });
        
        // Rate limiting between requests
        await new Promise(resolve => setTimeout(resolve, this.rateLimit));
        
      } catch (error) {
        results.push({
          input: vatData,
          result: null,
          valid: false,
          error: error.message
        });
      }
    }
    
    return results;
  }

  getCountryFromCode(code) {
    const countryMap = {
      'AT': 'Austria', 'BE': 'Belgium', 'BG': 'Bulgaria', 'HR': 'Croatia',
      'CY': 'Cyprus', 'CZ': 'Czech Republic', 'DK': 'Denmark', 'EE': 'Estonia',
      'FI': 'Finland', 'FR': 'France', 'DE': 'Germany', 'GR': 'Greece',
      'HU': 'Hungary', 'IE': 'Ireland', 'IT': 'Italy', 'LV': 'Latvia',
      'LT': 'Lithuania', 'LU': 'Luxembourg', 'MT': 'Malta', 'NL': 'Netherlands',
      'PL': 'Poland', 'PT': 'Portugal', 'RO': 'Romania', 'SK': 'Slovakia',
      'SI': 'Slovenia', 'ES': 'Spain', 'SE': 'Sweden', 'GB': 'United Kingdom'
    };
    
    return countryMap[code.toUpperCase()] || code;
  }

  async waitForRateLimit() {
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequest;
    
    if (timeSinceLastRequest < this.rateLimit) {
      await new Promise(resolve => setTimeout(resolve, this.rateLimit - timeSinceLastRequest));
    }
    
    this.lastRequest = Date.now();
  }
}

/**
 * Multi-Registry Search Orchestrator
 * Coordinates searches across all EU registries
 */
export class EURegistryOrchestrator {
  constructor() {
    this.registries = {
      german: new GermanRegistryAPI(),
      french: new FrenchSIRENEAPI(),
      dutch: new DutchKVKAPI(),
      vies: new EUVIESService()
    };
    
    this.searchCache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  async searchAllRegistries(query, options = {}) {
    const {
      countries = ['germany', 'france', 'netherlands'],
      maxResultsPerRegistry = 25,
      includeUK = true,
      activeOnly = true
    } = options;

    const cacheKey = `multi_${query}_${countries.join(',')}_${maxResultsPerRegistry}`;
    
    // Check cache
    if (this.searchCache.has(cacheKey)) {
      const cached = this.searchCache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.cacheTimeout) {
        console.log('[EU Registry] Using cached results');
        return cached.data;
      }
    }

    const searchResults = {
      companies: [],
      sources: [],
      errors: [],
      searchQuery: query,
      timestamp: new Date().toISOString(),
      totalResults: 0
    };

    // Search each requested registry
    const searchPromises = countries.map(async (country) => {
      try {
        let registryResult = null;
        
        switch (country.toLowerCase()) {
          case 'germany':
          case 'de':
            registryResult = await this.registries.german.searchCompanies({
              query,
              maxResults: maxResultsPerRegistry
            });
            break;
            
          case 'france':
          case 'fr':
            registryResult = await this.registries.french.searchCompanies({
              query,
              maxResults: maxResultsPerRegistry,
              activeOnly
            });
            break;
            
          case 'netherlands':
          case 'nl':
            registryResult = await this.registries.dutch.searchCompanies({
              query,
              maxResults: maxResultsPerRegistry,
              activeOnly
            });
            break;
        }
        
        if (registryResult && registryResult.companies) {
          searchResults.companies.push(...registryResult.companies);
          searchResults.sources.push({
            country,
            source: registryResult.source,
            count: registryResult.companies.length,
            status: 'success'
          });
        }
        
      } catch (error) {
        console.error(`Registry search failed for ${country}:`, error);
        searchResults.errors.push({
          country,
          error: error.message,
          timestamp: new Date().toISOString()
        });
        searchResults.sources.push({
          country,
          status: 'error',
          error: error.message
        });
      }
    });

    // Wait for all searches to complete
    await Promise.allSettled(searchPromises);
    
    // Remove duplicates and standardize data
    searchResults.companies = this.deduplicateCompanies(searchResults.companies);
    searchResults.totalResults = searchResults.companies.length;
    
    // Add ESG compliance indicators
    searchResults.companies = this.addESGIndicators(searchResults.companies);
    
    // Cache results
    this.searchCache.set(cacheKey, {
      data: searchResults,
      timestamp: Date.now()
    });
    
    return searchResults;
  }

  deduplicateCompanies(companies) {
    const seen = new Map();
    const unique = [];

    for (const company of companies) {
      const key = this.generateCompanyKey(company);
      
      if (!seen.has(key)) {
        seen.set(key, company);
        unique.push(company);
      } else {
        // Keep the record with more complete data
        const existing = seen.get(key);
        if (this.scoreCompleteness(company) > this.scoreCompleteness(existing)) {
          seen.set(key, company);
          const index = unique.findIndex(c => c.id === existing.id);
          if (index >= 0) {
            unique[index] = company;
          }
        }
      }
    }

    return unique;
  }

  generateCompanyKey(company) {
    const name = company.name.toLowerCase().replace(/[^\w\s]/g, '').trim();
    const country = company.country.toLowerCase();
    return `${name}_${country}`;
  }

  scoreCompleteness(company) {
    const fields = ['employees', 'turnover', 'industry', 'address', 'website', 'registrationDate'];
    return fields.reduce((score, field) => score + (company[field] ? 1 : 0), 0);
  }

  addESGIndicators(companies) {
    return companies.map(company => ({
      ...company,
      esgIndicators: {
        csrdApplicable: this.isCSRDApplicable(company),
        vsmeEligible: this.isVSMEEligible(company),
        industryRisk: company.esgMetadata?.industryESGRisk || this.assessGeneralIndustryRisk(company),
        complianceDeadline: company.esgMetadata?.complianceDeadline || this.estimateComplianceDeadline(company),
        priorityScore: this.calculateESGPriority(company)
      }
    }));
  }

  isCSRDApplicable(company) {
    return company.employees >= 500 || 
           (company.esgMetadata?.isListedCompany && company.employees >= 10);
  }

  isVSMEEligible(company) {
    const employees = company.employees || 0;
    const turnover = company.turnover || 0;
    
    return employees >= 10 && employees <= 250 && 
           turnover >= 2000000 && turnover <= 50000000;
  }

  assessGeneralIndustryRisk(company) {
    const industry = (company.industry || '').toLowerCase();
    const highRiskKeywords = ['manufacturing', 'energy', 'mining', 'chemical', 'oil'];
    const mediumRiskKeywords = ['construction', 'transport', 'automotive'];
    
    if (highRiskKeywords.some(keyword => industry.includes(keyword))) {
      return 'HIGH';
    }
    if (mediumRiskKeywords.some(keyword => industry.includes(keyword))) {
      return 'MEDIUM';
    }
    return 'LOW';
  }

  estimateComplianceDeadline(company) {
    if (company.employees >= 500) return '2025';
    if (company.esgMetadata?.isListedCompany) return '2026';
    return '2028';
  }

  calculateESGPriority(company) {
    let score = 0;
    
    // Compliance urgency
    if (this.isCSRDApplicable(company)) score += 40;
    else if (this.isVSMEEligible(company)) score += 25;
    
    // Industry risk
    switch (company.esgIndicators?.industryRisk || this.assessGeneralIndustryRisk(company)) {
      case 'HIGH': score += 30; break;
      case 'MEDIUM': score += 20; break;
      case 'LOW': score += 10; break;
    }
    
    // Company size (larger = more complex needs)
    if (company.employees > 250) score += 20;
    else if (company.employees > 50) score += 15;
    else score += 10;
    
    return Math.min(score, 100);
  }
}
// Export registry instances for use in services
export const germanRegistryAPI = new GermanRegistryAPI();
export const frenchSIRENEAPI = new FrenchSIRENEAPI();
export const dutchKVKAPI = new DutchKVKAPI();
export const euVIESService = new EUVIESService();
export const euRegistryOrchestrator = new EURegistryOrchestrator();